import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import { Snackbar } from '../../../../components/atoms';
import { Card as CardIcon } from '../../../../components/icons';
import { BottomBar, Hidden } from '../../../../components/layouts';
import { SectionGroup } from '../../../../components/molecules';
import { Currency } from '../../../../core/api/general/general.types';
import { basketSelectors } from '../../../../weborder/redux';
import { paymentSelectors } from '../../../../weborder/selectors';
import { ButtonPay, StripePayButtonAlt } from './components';
import PaymentOptions from './partials/PaymentOptions';
import { paymentOptionsUtils } from './utils';

type Props = {
  currency: Currency;
  currentLanguage: string;
  disabled?: boolean;
  errors?: {
    checkoutError?: any;
    paymentError?: any;
  };
  loading?: boolean;
  onSubmit: () => void;
  payments: ReturnType<typeof paymentSelectors.getAvailablePayments>;
  price: number;
  selectedPayment: ReturnType<
    typeof basketSelectors.getSelectedPaymentOrDefault
  >;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  paymentOptions: {
    '&-content': {
      paddingBottom: theme.spacing(10.5),

      [theme.breakpoints.up('md')]: {
        paddingBottom: theme.spacing(6),
      },
    },
  },
}));

export function PaymentSection(props: Props): JSX.Element {
  const {
    currency,
    currentLanguage,
    disabled,
    errors,
    loading,
    onSubmit,
    payments,
    selectedPayment,
    price,
    translate,
  } = props;

  const classes = useStyles();

  const noPaymentsAvailable = !payments.length;
  const placeText = paymentOptionsUtils.getPayButtonText(
    selectedPayment,
    translate,
  );

  const standardPayBtn = (
    <ButtonPay
      currency={currency}
      currentLanguage={currentLanguage}
      disabled={disabled}
      loading={loading}
      noPaymentsAvailable={noPaymentsAvailable}
      onSubmit={onSubmit}
      placeText={placeText}
      price={price}
      translate={translate}
    />
  );
  const stripePayBtn = (
    <StripePayButtonAlt
      currency={currency}
      currentLanguage={currentLanguage}
      data-fd="checkout-place-order"
      noPaymentsAvailable={noPaymentsAvailable}
      onClick={onSubmit}
      price={price}
    />
  );

  const isStripePayment =
    selectedPayment && selectedPayment.paymentId === 'stripe';
  const paymentButton = isStripePayment ? stripePayBtn : standardPayBtn;

  const { checkoutError, paymentError } = errors || {};
  const processingErrorMessage =
    checkoutError?.message || paymentError?.message;

  return (
    <>
      <SectionGroup
        className={classes.paymentOptions}
        icon={<CardIcon />}
        sectionName="payment-methods"
        title={translate('Payment_methods_title')}
      >
        {processingErrorMessage && (
          <Snackbar
            message={translate(processingErrorMessage, undefined, {
              missingTranslationMsg: processingErrorMessage,
            })}
            variant="warning"
          />
        )}

        <PaymentOptions
          disabled={disabled || loading}
          paymentOptions={payments}
          selectedPaymentId={selectedPayment && selectedPayment.paymentId}
          translate={translate}
        />
      </SectionGroup>

      <Hidden mdDown>{paymentButton}</Hidden>
      <Hidden mdUp>
        <BottomBar fixedOnly={['xs', 'sm']}>{paymentButton}</BottomBar>
      </Hidden>
    </>
  );
}
